/**
 * Project Service
 *
 * Handles all project CRUD operations.
 * Returns immutable updates (new arrays) rather than mutating state.
 */

import { looksLikeDMY, looksLikeISO, toISOFromDMY } from "../utils/date.js";

/**
 * Create a new project
 * @param {Object} projectData - Project data { name, description, startDate, endDate }
 * @param {Array} projects - Current projects array
 * @param {number} projectCounter - Current project counter
 * @returns {Object} { project, projects, projectCounter }
 */
export function createProject(projectData, projects, projectCounter) {
    const now = new Date().toISOString();
    const newProject = {
        id: projectCounter,
        name: projectData.name || "",
        description: projectData.description || "",
        startDate: projectData.startDate || "",
        endDate: projectData.endDate || "",
        tags: projectData.tags || [],
        createdAt: now,
        updatedAt: now,
    };

    const updatedProjects = [...projects, newProject];
    const updatedCounter = projectCounter + 1;

    return {
        project: newProject,
        projects: updatedProjects,
        projectCounter: updatedCounter
    };
}

/**
 * Update an existing project
 * @param {number} projectId - Project ID to update
 * @param {Object} projectData - Updated project data
 * @param {Array} projects - Current projects array
 * @returns {Object} { project, projects } - Updated project and array (or null if not found)
 */
export function updateProject(projectId, projectData, projects) {
    const projectIndex = projects.findIndex(p => p.id === parseInt(projectId, 10));
    if (projectIndex === -1) {
        return { project: null, projects };
    }

    const oldProject = projects[projectIndex];
    const updatedProject = {
        ...oldProject,
        name: projectData.name !== undefined ? projectData.name : oldProject.name,
        description: projectData.description !== undefined ? projectData.description : oldProject.description,
        startDate: projectData.startDate !== undefined ? projectData.startDate : oldProject.startDate,
        endDate: projectData.endDate !== undefined ? projectData.endDate : oldProject.endDate,
        tags: projectData.tags !== undefined ? projectData.tags : oldProject.tags,
    };

    const updatedProjects = [...projects];
    updatedProjects[projectIndex] = updatedProject;

    return {
        project: updatedProject,
        projects: updatedProjects
    };
}

/**
 * Update a single field of a project
 * @param {number} projectId - Project ID to update
 * @param {string} field - Field name to update
 * @param {any} value - New value for the field
 * @param {Array} projects - Current projects array
 * @returns {Object} { project, projects } - Updated project and array (or null if not found)
 */
export function updateProjectField(projectId, field, value, projects) {
    const projectIndex = projects.findIndex(p => p.id === parseInt(projectId, 10));
    if (projectIndex === -1) {
        return { project: null, projects };
    }

    const oldProject = projects[projectIndex];
    const updatedProject = { ...oldProject };

    // Handle date fields with conversion
    if (field === 'startDate' || field === 'endDate') {
        const iso = looksLikeDMY(value) ? toISOFromDMY(value)
            : looksLikeISO(value) ? value
            : "";
        updatedProject[field] = iso;
    } else {
        updatedProject[field] = value;
    }

    const updatedProjects = [...projects];
    updatedProjects[projectIndex] = updatedProject;

    return {
        project: updatedProject,
        projects: updatedProjects
    };
}

/**
 * Delete a project (optionally clearing task associations)
 * @param {number} projectId - Project ID to delete
 * @param {Array} projects - Current projects array
 * @param {Array} tasks - Current tasks array (optional)
 * @param {boolean} clearTaskAssociations - If true, set tasks' projectId to null
 * @returns {Object} { project, projects, tasks } - Deleted project and updated arrays
 */
export function deleteProject(projectId, projects, tasks = null, clearTaskAssociations = false) {
    const project = projects.find(p => p.id === parseInt(projectId, 10));
    if (!project) {
        return { project: null, projects, tasks };
    }

    const updatedProjects = projects.filter(p => p.id !== parseInt(projectId, 10));

    // Handle task associations if tasks array provided
    let updatedTasks = tasks;
    if (tasks && clearTaskAssociations) {
        updatedTasks = tasks.map(t => {
            if (t.projectId === parseInt(projectId, 10)) {
                return { ...t, projectId: null };
            }
            return t;
        });
    }

    return {
        project,
        projects: updatedProjects,
        tasks: updatedTasks
    };
}

/**
 * Get tasks associated with a project
 * @param {number} projectId - Project ID
 * @param {Array} tasks - Tasks array
 * @returns {Array} Tasks belonging to the project
 */
export function getProjectTasks(projectId, tasks) {
    return tasks.filter(t => t.projectId === parseInt(projectId, 10));
}

/**
 * Summarize a project's child task counts for the archive confirmation modal.
 * - open: any task not done/cancelled and not archived
 * - completed: status === 'done', not archived
 * - archived: archivedAt is set
 * Blocked is intentionally omitted (no codebase-wide convention for blocked).
 */
export function summarizeProjectTasks(projectId, tasks) {
    const pid = parseInt(projectId, 10);
    const counts = { open: 0, completed: 0, archived: 0, total: 0 };
    for (const t of tasks) {
        if (t.projectId !== pid) continue;
        if (t.isTemplate) continue;
        counts.total++;
        if (t.archivedAt) { counts.archived++; continue; }
        if (t.status === 'done') counts.completed++;
        else counts.open++;
    }
    return counts;
}

/**
 * Archive a project (and optionally child tasks). Returns immutable copies.
 * mode: 'none' | 'completed' | 'all'
 *   'none'      — only the project row is stamped archived_at
 *   'completed' — archive child tasks whose status is 'done'
 *   'all'       — archive every active (non-archived) child task
 * Status is never mutated.
 */
export function archiveProject(projectId, projects, tasks, mode = 'completed') {
    const pid = parseInt(projectId, 10);
    const now = new Date().toISOString();
    const project = projects.find(p => p.id === pid);
    if (!project) return { project: null, projects, tasks, affectedTaskIds: [] };

    const updatedProjects = projects.map(p =>
        p.id === pid ? { ...p, archivedAt: now, updatedAt: now } : p
    );

    const affectedTaskIds = [];
    let updatedTasks = tasks;
    if (mode !== 'none') {
        updatedTasks = tasks.map(t => {
            if (t.projectId !== pid) return t;
            if (t.archivedAt) return t;
            if (mode === 'completed' && t.status !== 'done') return t;
            affectedTaskIds.push(t.id);
            return { ...t, archivedAt: now, updatedAt: now };
        });
    }

    return {
        project: updatedProjects.find(p => p.id === pid),
        projects: updatedProjects,
        tasks: updatedTasks,
        affectedTaskIds,
        archivedAt: now,
        mode
    };
}

/**
 * Restore a project. Optionally restore its archived child tasks too.
 */
export function restoreProject(projectId, projects, tasks, restoreChildren = false) {
    const pid = parseInt(projectId, 10);
    const now = new Date().toISOString();
    const project = projects.find(p => p.id === pid);
    if (!project) return { project: null, projects, tasks, affectedTaskIds: [] };

    const updatedProjects = projects.map(p =>
        p.id === pid ? { ...p, archivedAt: null, updatedAt: now } : p
    );

    const affectedTaskIds = [];
    let updatedTasks = tasks;
    if (restoreChildren) {
        updatedTasks = tasks.map(t => {
            if (t.projectId !== pid) return t;
            if (!t.archivedAt) return t;
            affectedTaskIds.push(t.id);
            return { ...t, archivedAt: null, archivedByAutomationId: null, updatedAt: now };
        });
    }

    return {
        project: updatedProjects.find(p => p.id === pid),
        projects: updatedProjects,
        tasks: updatedTasks,
        affectedTaskIds,
        restoredChildren: restoreChildren
    };
}

/**
 * Validate project data
 * @param {Object} projectData - Project data to validate
 * @returns {Object} { valid: boolean, errors: string[] }
 */
export function validateProject(projectData) {
    const errors = [];

    if (!projectData.name || projectData.name.trim() === "") {
        errors.push("Project name is required");
    }

    // Validate date format if provided
    if (projectData.startDate && !looksLikeISO(projectData.startDate)) {
        errors.push("Invalid start date format (expected YYYY-MM-DD)");
    }

    if (projectData.endDate && !looksLikeISO(projectData.endDate)) {
        errors.push("Invalid end date format (expected YYYY-MM-DD)");
    }

    // Validate date logic if both provided
    if (projectData.startDate && projectData.endDate) {
        const start = new Date(projectData.startDate);
        const end = new Date(projectData.endDate);
        if (end < start) {
            errors.push("End date cannot be before start date");
        }
    }

    return {
        valid: errors.length === 0,
        errors
    };
}
